import axios, { AxiosInstance, AxiosError } from 'axios'
import axiosRetry from 'axios-retry'
import type { ApiResponse } from '@swim/types'

// ─── URL normalisation ─────────────────────────────────────────────────────────
// Strips trailing slash, ensures /api suffix is present exactly once.
// Callers write paths like '/offers' — never '/api/offers'.
//
// ❌ Gotcha: if you append '/api' in callers too you get /api/api/offers.
// KodeArk lesson: API Client Pattern — double-/api bug.
function resolveApiBase(): string {
  const raw = (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_API_URL ?? ''
  const base = raw.replace(/\/+$/, '')
  return base.endsWith('/api') ? base : `${base}/api`
}

// ─── Axios instance ────────────────────────────────────────────────────────────
const instance: AxiosInstance = axios.create({
  baseURL: resolveApiBase(),
  timeout: 30_000,
  withCredentials: true,          // Better Auth uses HTTP-only cookies — required
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// ─── Retry policy ──────────────────────────────────────────────────────────────
// Retries on network errors and 5xx only. Never retries 4xx — those are real errors.
axiosRetry(instance, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: AxiosError) => {
    // 503 = feature intentionally disabled — not a transient error, never retry
    if (error.response?.status === 503) return false
    return axiosRetry.isNetworkError(error) || axiosRetry.isRetryableError(error)
  },
  onRetry: (retryCount, error) => {
    console.warn(`[swimApi] Retry ${retryCount} — ${error.config?.url}`)
  },
})

// ─── Response normalisation ───────────────────────────────────────────────────
// Every response is normalised to ApiResponse<T> before it reaches callers.
// No raw Axios errors leak out — errors are always { success: false, error: string }.
async function request<T>(
  method: 'get' | 'post' | 'patch' | 'delete',
  path: string,
  body?: unknown,
  headers?: Record<string, string>,
): Promise<ApiResponse<T>> {
  try {
    const response = await instance.request<T>({
      method,
      url: path,
      data: body,
      ...(headers ? { headers } : {}),
    })
    return { success: true, data: response.data }
  } catch (err) {
    const axiosErr = err as AxiosError<{ message?: string; error?: string }>
    const rawMessage =
      axiosErr.response?.data?.message ??
      axiosErr.response?.data?.error ??
      axiosErr.message ??
      'Unknown error'
    const message = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage)

    if (axiosErr.response) {
      if (axiosErr.response.status >= 500) {
        console.error(`[swimApi] Server error ${axiosErr.response.status} — ${path}:`, message)
      } else if (axiosErr.response.status !== 401 && axiosErr.response.status !== 404) {
        // 403/409/422 and other unexpected 4xx — warn for visibility (401/404 are expected, not logged)
        console.warn(`[swimApi] Client error ${axiosErr.response.status} — ${path}:`, message)
      }
    } else {
      // Network error — no HTTP response (DNS failure, timeout, connection refused)
      console.warn(`[swimApi] Network error — ${path}:`, message)
    }

    return { success: false, error: message, status: axiosErr.response?.status }
  }
}

// ─── HTTP methods (no PUT — SWIM uses PATCH for updates, POST for creation) ───
// Architecture: see ADR-036 — vault/12-technical/ADR/ADR-036-swimapi-single-http-client.md
// ─── URL helper for raw fetch() calls ─────────────────────────────────────────
// Use this instead of hardcoding '/api/...' paths — resolves the correct API
// base (VITE_API_URL) so calls work in production across separate domains.
// Path should start with '/' and NOT include '/api' — e.g. '/claims/abc/download'
export function getApiUrl(path: string): string {
  const base = resolveApiBase()
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${base}${normalized}`
}

export const swimApi = {
  /**
   * GET — read a resource.
   * @example swimApi.get<Offer>('/offers/clxxx')
   */
  get: <T>(path: string) => request<T>('get', path),

  /**
   * POST — create a new resource.
   * This includes append-only ledger operations (burnDrops, creditDrops, reissueDrops)
   * which each INSERT a new DropTransaction row — never an UPDATE.
   * Optional `headers` are added to the default ones for this call only.
   * @example swimApi.post<Claim>('/claims', { offerId, memberId })
   */
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('post', path, body, headers),

  /**
   * PATCH — partial update of an existing resource.
   * Used for: updating offer fields, transitioning claim status, updating account.
   * Never used for ledger operations.
   * @example swimApi.patch<Offer>('/offers/clxxx', { title: 'New title' })
   */
  patch: <T>(path: string, body?: unknown) => request<T>('patch', path, body),

  /**
   * DELETE — remove a resource.
   * Rare in SWIM — most removals are status transitions (ARCHIVED, REVOKED).
   * Only used where hard deletion is intentional.
   * Optional body for deletion confirmations (e.g., { confirm: 'DELETE' }).
   */
  delete: <T>(path: string, body?: unknown) => request<T>('delete', path, body),
}

export type { ApiResponse }
